/**
 * rangeprimitive.ts — draw dealing-range rays and OTE bands on the canvas.
 *
 * THE SAME RULE AS EVERY OTHER FILE HERE: this computes no ICT rule. It receives prices,
 * timestamps and colours from `overlays.ts` (which received them from the Python API) and
 * turns them into pixels. The only arithmetic below is coordinate conversion.
 *
 * WHY A PRIMITIVE INSTEAD OF `series.createPriceLine`
 * ---------------------------------------------------
 * Andrei, 2026-07-27: "there's no need to draw horizontal lines across the entire
 * overlay. It is enough to do it from the respected high/low to the right. The same with
 * OTE zone."
 *
 * `createPriceLine` cannot do that — it draws the full pane width, always, and it cannot
 * fill a band at all. A full-width line is also a false claim: the level did not exist
 * before the swing that set it, so drawing it leftwards shows a level in a past that
 * never had one. lightweight-charts v4's series-primitive API (`attachPrimitive`) is the
 * supported way to draw custom geometry in the price pane, so the levels start at the bar
 * that set them and run to the right edge.
 *
 * `drawBackground` vs `draw`: the OTE fill goes in the BACKGROUND layer so candles and
 * wicks stay readable through it (Andrei asked for transparent zones, and a foreground
 * fill would sit on top of the very price action you are checking the zone against). The
 * rays and their labels go in the foreground.
 */
import type {
  IChartApi,
  ISeriesApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';
import type { RangeDrawing, ZoneBand } from './overlays';

/**
 * The fancy-canvas rendering scope, structurally re-declared.
 *
 * fancy-canvas is lightweight-charts' own rendering dependency, not a direct dependency
 * of this app, so its types are described here rather than imported from a package the
 * manifest does not list.
 */
type Scope = {
  context: CanvasRenderingContext2D;
  horizontalPixelRatio: number;
  verticalPixelRatio: number;
  bitmapSize: { width: number; height: number };
};
type Target = { useBitmapCoordinateSpace: (f: (scope: Scope) => void) => void };

/** Rays + bands for the ranges overlay, at both swing degrees.
 *
 *  One primitive holds BOTH degrees' geometry: it is attached once and its data replaced
 *  on every overlay refresh, so there is no attach/detach churn per refresh and no way
 *  for a stale primitive to survive a series switch. */
export class RangePrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<SeriesType, Time> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _data: RangeDrawing = { rays: [], bands: [] };
  private _hiddenBandLabels = 0;
  private _drawnBandLabels = 0;
  private readonly _views: ISeriesPrimitivePaneView[];

  constructor() {
    // Two views: the band fill is drawn UNDER the candles (`drawBackground`, zOrder
    // 'bottom'), the rays and labels OVER them. Splitting them is what keeps a
    // transparent zone from washing out the price action it frames.
    this._views = [
      {
        zOrder: () => 'bottom',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: () => {},
          drawBackground: (t) => this._drawBands(t as unknown as Target),
        }),
      },
      {
        zOrder: () => 'top',
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: (t) => this._drawRays(t as unknown as Target),
        }),
      },
    ];
  }

  attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    return this._views;
  }

  /** The bands currently held, for the "zones off screen -> show them" control.
   *
   *  The DRAWN truth rather than a recomputation: the control has to move the price scale
   *  onto the zones this primitive was actually given, and a caller that re-derived them
   *  from the payload could zoom to a band that is not on the chart at all.
   *
   *  ONE SLOT NOW. There used to be a `leadBands` slot drawn underneath everything else, and
   *  the WEEK BOX was its only producer -- deleted with the rest of the weekly geometry (card
   *  t_70188040). It existed so the live loop could rewrite that one band on every `updateBar`
   *  without re-running ~7 detector endpoints; with no such band there is nothing to rewrite,
   *  and a second slot with no producer is a second thing to keep correct. */
  get bands(): readonly ZoneBand[] {
    return this._data.bands;
  }

  /** Replace the geometry and repaint. `{rays:[],bands:[]}` clears the overlay. */
  setData(d: RangeDrawing): void {
    this._data = d;
    this._requestUpdate?.();
  }

  /**
   * `(time, price)` -> bitmap `{x, y}`, or null when either is off-scale.
   *
   * The x is CLAMPED to the pane, never dropped: when the anchor bar is scrolled off to
   * the left the level still applies to every visible bar, so it is truncated. Dropping
   * it would read as "there is no level here" — the one answer this app must not fake.
   * Defaulting a missing coordinate to 0, by contrast, would draw the full-width line
   * this whole primitive exists to avoid, so an off-scale value aborts the shape.
   */
  private _at(time: Time, price: number, hr: number, vr: number) {
    const x = this._chart?.timeScale().timeToCoordinate(time);
    const y = this._series?.priceToCoordinate(price);
    return x == null || y == null ? null : { x: Math.max(0, x * hr), y: y * vr };
  }

  /** The top-left of a `fullHeight` band: its anchor's x, at y 0. Null off the time scale. */
  private _fullTop(time: Time, hr: number) {
    const x = this._chart?.timeScale().timeToCoordinate(time);
    return x == null ? null : { x: Math.max(0, x * hr), y: 0 };
  }

  /**
   * One band's bitmap rectangle, or null when it cannot be drawn.
   *
   * SHARED by the background pass (which fills it) and the foreground pass (which may
   * write a caption INSIDE it — see `_insideLabel`). One function, because a caption
   * placed against a re-derived rectangle can land outside the box that was painted.
   */
  private _bandRect(b: ZoneBand, hr: number, vr: number,
                    canvasW: number, canvasH: number): BandRect | null {
    // `fullHeight` (a TIME window, card t_42cdbde6, 2026-10-14): top of the pane to the bottom,
    // `lo`/`hi` ignored. Only the vertical extent changes; the horizontal logic below is shared.
    const top = b.fullHeight ? this._fullTop(b.fromTime, hr) : this._at(b.fromTime, b.hi, hr, vr);
    const bot = b.fullHeight
      ? (top ? { x: top.x, y: canvasH } : null)
      : this._at(b.fromTime, b.lo, hr, vr);
    if (!top || !bot) return null;
    // A band that ENDED stops at its end bar. Null `toTime` = still live =
    // run to the right edge, which is what the OTE band and an unfilled FVG
    // both want. The x conversion happens here; the DECISION is `bandRect`.
    //
    // A GROWING band needs nothing new: the week box passes the CURRENT bar's time as
    // `toTime`, so the right edge tracks it on every refresh with no live-edge branch.
    //
    // BITMAP SPACE: `_at` has ALREADY multiplied `top.x` by `hr`, while `timeToCoordinate`
    // returns CSS pixels and needs it.
    //
    // (A `spanPx` branch -- a fixed pixel-length LEVEL -- used to outrank both. Its one consumer was
    // the live IDM's 44px stub, which Andrei could not see; card `t_0cdae4dc` runs the IDM to the
    // right edge, so the branch went with it.)
    const bandEndX = b.toTime == null
      ? null
      : (() => {
          const x = this._chart?.timeScale().timeToCoordinate(b.toTime);
          return x == null ? null : x * hr;
        })();
    return bandRect({
      left: top.x, top: top.y, bottom: bot.y,
      endX: bandEndX,
      canvasW, canvasH,
      // SCALED BY `hr`, like the time bound above, and for the same reason. This is a
      // READABILITY floor, and readability is measured in CSS pixels -- the ones on
      // Andrei's screen -- while everything reaching `fillRect` here is BITMAP pixels.
      // Unscaled, the floor shrank by a factor of `hr` on every retina pane: `MIN_BAND_PX`
      // bitmap pixels is HALF that many CSS pixels at `hr = 2`, so a floor written to be
      // findable by eye arrived on screen at half its intended size. The comment above
      // this block already warns about exactly this asymmetry for the time bound; the floor had
      // the same hole and no test reached it, because `bandRect` is pure and every test
      // called it at an implied `hr` of 1.
      minWidth: MIN_BAND_PX * hr,
      exactSpan: b.exactSpan,
    });
  }

  private _drawBands(target: Target): void {
    if (!this._series || !this._data.bands.length) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                      verticalPixelRatio: vr, bitmapSize }) => {
      for (const b of this._data.bands) {
        const r = this._bandRect(b, hr, vr, bitmapSize.width, bitmapSize.height);
        if (r == null) continue;      // wholly outside the visible price range
        ctx.fillStyle = b.fill;
        ctx.fillRect(r.left, r.top, r.right - r.left, r.bottom - r.top);
        // The two horizontal edges only. A full rectangle outline would add a
        // vertical stroke at the anchor that reads as a level of its own.
        ctx.strokeStyle = b.border;
        ctx.lineWidth = Math.max(1, Math.floor(hr));
        // A dashed LEVEL (the live IDM, card `t_0cdae4dc`) -- scoped by save/restore so the dash
        // cannot leak into the next band's solid edges.
        ctx.save();
        if (b.dashed) ctx.setLineDash([6 * hr, 4 * hr]);
        ctx.beginPath();
        for (const y of [r.top, r.bottom]) {
          ctx.moveTo(r.left, y);
          ctx.lineTo(r.right, y);
        }
        ctx.stroke();
        ctx.restore();
        // CE bisector, dashed, per Andrei's reference diagram: the box is the
        // zone, the midline is the entry. Bounded exactly like the box.
        if (b.midline != null) {
          const mid = this._at(b.fromTime, b.midline, hr, vr);
          if (mid) {
            ctx.save();
            ctx.setLineDash([4 * hr, 4 * hr]);
            ctx.beginPath();
            ctx.moveTo(r.left, mid.y);
            ctx.lineTo(r.right, mid.y);
            ctx.stroke();
            ctx.restore();
          }
        }
      }
    });
  }

  private _drawRays(target: Target): void {
    if (!this._series) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                      verticalPixelRatio: vr, bitmapSize }) => {
      const right = bitmapSize.width;
      // Plates already placed this frame, so a later label can be nudged off an
      // earlier one instead of printing on top of it. MEASURED collision, not a
      // hypothetical: `L? 1223.4 (open leg, 4 bars)` landed 7px from
      // `SL 1309.9 swept` on SNDK 1D, and the plates are 13px tall. The break
      // labels needed the same treatment and shipped broken three times without
      // it — a label pass with no collision test is a known-bad pattern here.
      const placed: PlateBox[] = [];

      for (const r of this._data.rays) {
        const p = this._at(r.fromTime, r.price, hr, vr);
        if (!p) continue;
        ctx.save();
        ctx.strokeStyle = r.color;
        ctx.lineWidth = Math.max(1, Math.round(r.width * hr));
        if (r.dashed) ctx.setLineDash([4 * hr, 4 * hr]);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(right, p.y);
        ctx.stroke();
        ctx.restore();
        this._label(ctx, r.label, r.color, p, hr, vr, right, placed, false, bitmapSize.height);
      }

      // Band labels ride in the foreground so they are not washed out by their own fill.
      //
      // DROPPED rather than nudged when there is no room, and that asymmetry with the
      // rays above is deliberate. Bands used to number 2 (one OTE per degree); with the
      // FVG boxes they reach 43 on a single series (measured: median 9, p90 23 live gaps
      // over 117 cached series). `placePlate` never drops — it nudges up to `maxTries`
      // and then draws anyway — which is right for a LEVEL (few of them, and the price
      // is the whole point) and wrong for 43 zones, where it produces exactly the
      // overlapping confetti the break labels shipped three times. A box carries its
      // information geometrically: height, position and colour are all visible with no
      // text at all. So a colliding zone label is withheld and COUNTED, and the sidebar
      // states the count — the same disclosure contract as `BreakPrimitive.hiddenLabels`.
      this._hiddenBandLabels = 0;
      this._drawnBandLabels = 0;
      for (const b of this._data.bands) {
        // A `fullHeight` band's plate sits at its top-left, a plate's height below the pane top.
        const full = b.fullHeight ? this._fullTop(b.fromTime, hr) : null;
        const at = full ? { x: full.x, y: FULL_PLATE_Y * vr } : this._at(b.fromTime, b.hi, hr, vr);
        if (!at) continue;
        // `plateRight`: the plate sits at the pane's RIGHT EDGE (`placePlate` flips it inside), for
        // a level that runs there -- see `ZoneBand.plateRight`.
        const p = b.plateRight ? { x: right, y: at.y } : at;
        // OFF-PANE BANDS GET NO LABEL, and are not counted as suppressed either — the
        // same refusal `_drawBands` applies to the box itself. `priceToCoordinate`
        // extrapolates past the visible range (measured: y up to 537 on a 298px pane),
        // so without this a zone nobody can see would still stack a plate at the edge
        // and inflate the "hidden" count with bands that were never candidates.
        if (p.y < 0 || p.y > bitmapSize.height) continue;
        // NO CAPTION ASKED FOR, so none is drawn and none is counted as suppressed -- it was
        // never a candidate, exactly like the off-pane bands above. `setupBands` sends an empty
        // label for a SPENT setup (2026-08-09: 34 captions on BTC/15m, 7 of them live), and
        // without this the plate would still paint a 6px translucent chip and consume a row in
        // `placed`, pushing a LIVE zone's caption out for a band that had nothing to say.
        if (!b.label) continue;
        const before = placed.length;
        // A right-edge LEVEL is never dropped on a collision (`dropIfObstructed` false): a level
        // whose label is withheld reads as "there is no level here".
        this._label(ctx, b.label, b.border, p, hr, vr, right, placed, !b.plateRight,
          bitmapSize.height);
        if (placed.length > before) this._drawnBandLabels += 1;
        else this._hiddenBandLabels += 1;
      }
    });
  }

  /** Zone labels withheld for want of room this frame. Disclosed in the sidebar. */
  get hiddenBandLabels(): number {
    return this._hiddenBandLabels;
  }

  /** Zone labels actually painted this frame. */
  get drawnBandLabels(): number {
    return this._drawnBandLabels;
  }

  /** Text just right of the anchor (or flipped inside the pane when it would overflow -- how a
   *  `plateRight` band anchored AT the right edge gets its plate), on a translucent plate.
   *
   *  `dropIfObstructed` is what separates a LEVEL from a ZONE. A level's label is
   *  nudged and drawn regardless (few of them, and the price is the point); a zone's
   *  is withheld when no clear row exists, because 43 boxes' worth of plates is the
   *  overlapping confetti the break labels shipped three times. Returns whether it
   *  painted, so the caller can COUNT the suppression instead of hiding it. */
  private _label(
    ctx: CanvasRenderingContext2D,
    text: string,
    color: string,
    at: { x: number; y: number },
    hr: number,
    vr: number,
    canvasW: number,
    placed: PlateBox[],
    dropIfObstructed = false,
    canvasH?: number,
  ): boolean {
    ctx.save();
    // BOLD, and it is a LEGIBILITY fix rather than emphasis. Andrei's screenshot showed the
    // `IDM` plate reading as `ICM` for four days. It is not a clip and not a `measureText`
    // mismatch -- the plate is 3.07px wider than the ink at vr=1 and there is no `ctx.clip()`
    // in this file. It is glyph RASTERISATION at 10 CSS px: cap height is 8 device pixels at
    // vr=1, and measured on this exact font stack only **4 of 848 inked pixels reached the
    // real glyph colour (0.5%)** -- 99% of the word was partial-alpha haze, and the `D`'s
    // closing stem landed entirely inside it. The counter (the enclosed hole) survived, so it
    // never blobbed; specifically the bowl's right stem dissolved, leaving a `C`.
    //
    // `bold` at the SAME 10px because it is the smallest fix that works. Measured, IDM at
    // vr=1: solid ink 4 -> 39 px (8.2x), solid fraction 0.045 -> 0.368, and the solid pixels
    // that distinguish `IDM` from `ICM` go 2 -> 17. It costs **+0.67px of plate width** (24.37
    // -> 25.04), which is the least of any candidate: `11px` alone reaches only 5 distinguishing
    // pixels for +1.84px, and `bold 11px` buys little more legibility for +2.57px. Footprint
    // matters because the plate is anchored at `b.hi` and a wider one collides sooner, which
    // `_label`'s `dropIfObstructed` pays for by HIDING a caption -- see `hiddenBandLabels`.
    // `levelplate.ts:102` and `breakprimitive.ts:330` already build the same bold path, so
    // this is the established shape for this font stack, not a new one.
    ctx.font = `bold ${Math.round(10 * vr)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    const padX = 3 * hr;
    const w = ctx.measureText(text).width + padX * 2;
    const h = 13 * vr;
    const box = placePlate({
      anchorX: at.x, anchorY: at.y, w, h, canvasW,
      // `canvasH` so a nudge cannot march the plate off the top of the pane -- see
      // `placePlate`'s vertical clamp.
      canvasH, gap: 4 * hr, step: h + 2 * vr, placed,
    });
    if (dropIfObstructed && !box.clear) {
      ctx.restore();
      return false;
    }
    placed.push(box);
    ctx.fillStyle = 'rgba(19, 23, 34, 0.72)';
    ctx.fillRect(box.left, box.top, w, h);
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, box.left + padX, box.top + h / 2);
    ctx.restore();
    return true;
  }

}

/** A placed plate's bitmap box.
 *
 *  `clear` says whether the returned position is actually free of every plate in
 *  `placed`. `placePlate` never refuses — it nudges up to `maxTries` and then returns
 *  its last position anyway, because a dropped LEVEL label reads as "there is no level
 *  here". A caller drawing many ZONES wants the opposite (see `_label`'s
 *  `dropIfObstructed`), and that decision needs this fact rather than a re-derivation
 *  of the collision test. */
export type PlateBox = {
  left: number; right: number; top: number; bottom: number;
  /** False when this box still overlaps an already-placed plate. */
  clear: boolean;
};

/** A band's bitmap rectangle. */
export type BandRect = { left: number; right: number; top: number; bottom: number };

/**
 * WHERE a band's rectangle goes — pure, so the BOUNDS can be TESTED without a canvas.
 *
 * This exists because of the FVG box (2026-07-28). Bands used to be drawn straight to
 * `bitmapSize.width` inside the render loop, which is correct for a live zone and a
 * false claim for a dead one: an FVG that was filled at bar N stopped existing at bar
 * N, and a box running past it says the zone is still there. Same reasoning as the
 * module docstring's rejection of full-width lines, one axis over.
 *
 * The rules, all three measured against real payload shapes rather than assumed:
 *
 *  1. `endX == null` means STILL LIVE -> run to the pane's right edge. That is what
 *     an unfilled gap and the OTE band both want, so null is not a missing value.
 *  2. A bounded band is never drawn NARROWER than 1px. `timeToCoordinate` returns the
 *     same x for two bars once the zoom drops below 1px/bar (BTC 1D: 0.41 px/bar in a
 *     896px pane), so a same-bar fill would otherwise vanish — and a zone that
 *     vanishes reads as "the detector found nothing", the one answer this app must
 *     not fake.
 *  3. Nor SHORTER than 1px, for the same reason on the other axis — and this one was
 *     measured on the running chart, not reasoned about. Instrumenting `fillRect` on
 *     the live BTC 1D feed with the FVG boxes on: **14 of 40 zones were sub-pixel
 *     tall** (0.03px to 0.97px; 35%), so their fill painted nothing and the box
 *     collapsed back into exactly the CE hairline the boxes replaced. 1 device pixel
 *     is the floor of what a canvas can show, not a calibrated threshold: the zone is
 *     still drawn at its own price, and zooming the price scale expands it.
 *  4. A band wholly OUTSIDE the visible price range is REFUSED (`null`), and one that
 *     straddles an edge is CLIPPED to the pane. Also measured, and it was the reason
 *     two vision passes reported "no rectangles" while 39 boxes sat in the draw list:
 *     **24 of 39 zones painted at y up to 537 on a 298px-tall pane**, i.e. off-canvas
 *     entirely. `priceToCoordinate` happily extrapolates past the visible range, so an
 *     FVG from 2020 lands hundreds of pixels below the pane and the 15 that WERE
 *     on-screen looked like nothing next to the labels. Refusing beats clamping here:
 *     a clamped band would pile every off-scale zone onto the pane's edge as a false
 *     1px shelf. This is the y-axis twin of the x-clamp in `_at`, which had the same
 *     hole — it clamped x and left y unbounded.
 *  5. `top`/`bottom` are ORDERED. Bitmap y grows downward, so the higher PRICE has the
 *     smaller y; a caller that passed them the other way round (or a band whose hi/lo
 *     were swapped) would otherwise produce a negative-height `fillRect`, which canvas
 *     silently draws as nothing.
 *  6. `minWidth` is the caller's, in the caller's own pixel space. See `MIN_BAND_PX`.
 *  7. The floor is LANDSCAPE, computed against this band's own drawn height. See
 *     `MIN_BAND_ASPECT` — a constant width floor is what produced the sliver.
 *  8. `exactSpan` opts a band out of the ASPECT floor (only the `minWidth` absolute floor
 *     stays). It was deleted with the week box (card t_70188040) and is back for the session
 *     boxes (card t_5b2017b5, 2026-10-10): their x-extent is a FACT -- Frankfurt is one hour --
 *     and the 2x-height floor would paint a tall Frankfurt box hours wide, into London.
 */
export function bandRect(o: {
  left: number; top: number; bottom: number;
  endX: number | null; canvasW: number; canvasH?: number;
  minWidth?: number;
  exactSpan?: boolean;
}): BandRect | null {
  const { left, endX, canvasW, canvasH } = o;
  let top = Math.min(o.top, o.bottom);
  let bottom = Math.max(o.top, o.bottom);
  if (canvasH != null) {
    // Wholly above or wholly below the pane: there is nothing to draw, and drawing it
    // clamped would invent a shelf at the edge where no zone exists.
    if (bottom < 0 || top > canvasH) return null;
    top = Math.max(0, top);
    bottom = Math.min(canvasH, bottom);
  }
  bottom = Math.max(top + 1, bottom);
  // THE FLOOR IS DERIVED FROM THE HEIGHT, and that is the whole fix. See
  // `MIN_BAND_ASPECT`: the height is settled first (clipped and floored, above), so the
  // stub is measured against the shape actually being painted rather than against the
  // band's nominal price span.
  //
  // `exactSpan` IS THE ONE OPT-OUT, for a band whose width is a FACT rather than a readability
  // shape. The week box was the first (the 2x-height floor painted it 39.3 days wide, measured,
  // card t_420df4d4) and went with that box (t_70188040); the session boxes are the second
  // (t_5b2017b5): a Frankfurt box is one hour wide by definition, and widening it would draw
  // Frankfurt over London's bars.
  const minWidth = o.exactSpan
    ? (o.minWidth ?? MIN_BAND_PX)
    : Math.max(o.minWidth ?? MIN_BAND_PX, (bottom - top) * MIN_BAND_ASPECT);
  if (endX == null) return { left, right: canvasW, top, bottom };
  const right = Math.min(canvasW, Math.max(endX, left + minWidth));
  // AND IT GROWS LEFTWARD WHEN THE RIGHT EDGE IS IN THE WAY, which the aspect floor alone
  // did not do and is the last way the sliver came back. MEASURED on the real 1D payload at
  // a 1200-bar window: one spent order block anchored at x=441 on a 443px pane drew 2.2x2.9
  // -- taller than wide, caps and all, Andrei's exact artifact -- because `Math.min(canvasW,
  // ...)` truncated the floored width to the 2px of pane that was left. Every other floored
  // band on that frame came out landscape, so this is a boundary case and not a second
  // mechanism. A stub is a READABILITY shape with no claim about duration (its honest width
  // is zero, or sub-pixel), so extending it back from the pane edge invents nothing that
  // extending it forward did not already invent.
  return { left: Math.max(0, Math.min(left, right - minWidth)), right, top, bottom };
}

/**
 * The narrowest a BOUNDED band may be drawn, in CSS pixels.
 *
 * Andrei, 2026-08-09: the transparent trading area should always display. The zone he
 * could not see was there in the draw list and one pixel wide. A setup that is no longer
 * live is bounded at its OWN start bar (the API serves no bar for when price consumed it,
 * unlike an order block's mitigation), so `endX == left` and the old `left + 1` floor
 * painted a ONE-PIXEL column of 30%-alpha fill: present in the draw list, invisible on
 * screen. A zone that vanishes reads as "the detector found nothing", which is the one
 * answer this app must not fake. Any zone bounded on the SAME bar it starts on hits this.
 *
 * THREE PIXELS, and it is deliberately back at three after a day at eighteen. This value
 * is only the ABSOLUTE floor now; the shape is set by `MIN_BAND_ASPECT`, which is where
 * the readability decision moved. Three is what keeps a band that is genuinely one pixel
 * tall (14 of 40 measured on BTC 1D) from being a bare vertical tick, and nothing more.
 *
 * ponytail: a fixed pixel floor, not a bar count. Serve a spent/mitigation time for
 * setups and the bound becomes real geometry instead of a readability minimum.
 */
export const MIN_BAND_PX = 3;
/** CSS-pixel y of a `fullHeight` band's plate: just below the pane top, so it clears the edge
 *  (card t_42cdbde6, 2026-10-14). */
const FULL_PLATE_Y = 12;

/**
 * A STUB ZONE IS DRAWN LANDSCAPE: at least this many pixels wide for every pixel tall.
 *
 * *** THIS IS ANDREI'S ITEM REPORTED TWICE, AND THE CAUSE WAS THE FIX BEFORE IT. ***
 *
 * "I still see these strange thin red and green vertical line artifacts." He is right, and
 * they are ours. MEASURED off his frame's raw pixel columns rather than described by a
 * vision pass (two passes read them as "1px wide, no caps" and both were wrong at that
 * scale): x=43, y318-329, interior RGB (57,42,61) over background (20,22,35), TWO pixels
 * wide, with BRIGHTER cap rows at y318-319 (53,51,65) and y328-329 (46,37,54), flat
 * background either side and no candle anywhere near. A second at x=119-120, y429-444,
 * teal interior (37,47,57), caps at y430-431 and y443. A CANDLE WICK HAS NO CAPS: those
 * caps are `_drawBands`' own two horizontal edge strokes at `r.top`/`r.bottom`, which is
 * the proof the shape is drawn by this file and not by the series.
 *
 * WHY A CONSTANT WIDTH FLOOR CANNOT FIX THIS, which is the actual root cause and the
 * reason raising 3 to 18 was the wrong shape. A zone is recognisable as a zone when it is
 * WIDER THAN IT IS TALL, and its height is not a constant -- it is the price span mapped
 * through a price scale the user zooms. MEASURED on the real 1D BTC payload's own window,
 * the 91 spent setups: median drawn height 4.1px, p90 9.1px, max 30.1px, min 0.4px. So a
 * single number is wrong for the whole population at once, in both directions:
 *
 *     at MIN_BAND_PX = 3   a 12px-tall zone drew 3x12  -- a vertical line, HIS ARTIFACT
 *     at MIN_BAND_PX = 18  a 2px-tall zone drew 18x2   -- and there are many of those:
 *                          43 of 91 spent setups are under 4px tall on this payload
 *
 * Eighteen did not remove the artifact class, it moved it to the other axis: an 18x2 chip
 * is a thin HORIZONTAL dash, unrelated to any zone height, and it is 18px of false extent
 * for a zone whose real bound is one bar. It also collided -- at a 400-bar window the 18
 * on-pane stubs overlap a neighbour on 9 of 17 adjacent pairs, and at 1200 bars on 46 of
 * 50, so the stubs merge into a band that claims continuous zone coverage where there are
 * discrete zones. THE FLOOR MUST TRACK THE HEIGHT. Then a tall zone gets a wide stub, a
 * short zone gets a narrow one, and both read as a small box rather than as a stroke.
 *
 * TWO, not more. `2 : 1` is the cheapest ratio that is unambiguously landscape at every
 * height in the measured population, and it is bounded by the same collision measurement:
 * a stub is 2x its own height, so at the median 4.1px it is 8px, which is HALF what a flat
 * 18 cost and does not merge with its neighbour at the windows where 18 did. It is also
 * still visibly a stub -- the real spent bounds on this payload span whole bars (order
 * blocks: median 18 days between formation and mitigation), so nothing with honest extent
 * is touched: the floor is a MINIMUM and `Math.max` leaves every wider band exactly alone.
 *
 * WHY NOT SIMPLY STOP DRAWING THEM (the tempting one-line fix): because that is the exact
 * defect this floor was added to fix, and it would re-hide the zones 53ba07a revealed. On
 * this payload 91 of 98 setups are spent and 173 of 201 order blocks, so refusing a
 * width-less band empties the overlay of nearly everything it draws. Nor may the check be
 * "is it live" -- the two populations are NOT the same defect and only one is width-less:
 *
 *     setup (spent)        bound == start bar, honest width EXACTLY 0 at every zoom
 *     order_blocks (spent) bound is a real mitigation bar, honest width 3.7px at a
 *                          120-bar window and 0.4px at 1200 -- REAL extent, merely
 *                          sub-pixel at that zoom, and it must not be widened to a stub
 *
 * An aspect floor serves both without a branch, because it asks the only question that
 * matters at paint time -- is this shape legible -- and answers it in the pixels actually
 * being painted. A `live`/`spent` branch would have needed the payload semantics here, in
 * a file whose whole contract is that it computes no rule.
 *
 * WHY NOT AN HONEST WIDTH FROM THE PAYLOAD: there is none for a setup, and this was
 * checked field by field rather than assumed. The setup row carries `break_time`,
 * `idm_time`, `swept_time` and `time`, and on all 91 spent rows `time` ALREADY EQUALS
 * `swept_time` -- the sweep is the box's START, so it cannot also be its end, and
 * `break_time` and `idm_time` both precede it (`idm_time` < `break_time` <= `swept_time`
 * on 71 of 91). No field bounds the END. Order blocks DO carry `mitigated_time` and
 * `setupBands`' sibling already uses it; that is why they never had a zero-width case.
 *
 * ponytail: a ratio fitted to one payload's measured height distribution, not a rule from
 * the source. Serve a spent time for setups and the stub disappears on its own.
 */
export const MIN_BAND_ASPECT = 2;

/**
 * WHERE a level's label plate goes — pure, so the decision can be TESTED.
 *
 * Two defects this encodes, both measured on the live chart by instrumenting
 * canvas `fillText` (what is in the draw list is not what is on the screen):
 *
 *  1. **Off-pane at the right edge.** The plate was placed at `anchorX + gap` with
 *     no clamp. A level whose anchor is the newest bar — which is *always* true of
 *     the open leg's provisional extreme — printed at x=909 on a 910px canvas: 1px
 *     of room, invisible. The swept range labels looked fine only because their
 *     anchors sit further left, so the bug was latent for every right-edge level.
 *     Fix: flip to the LEFT of the anchor when it would overflow, then hard-clamp.
 *  2. **Overlapping plates.** `L? 1223.4 (open leg, 4 bars)` landed 7px from
 *     `SL 1309.9 swept` with 13px-tall plates. Fix: nudge vertically until clear.
 *     The break-label work shipped three times without a collision test and was
 *     wrong every time; a label pass without one is a known-bad pattern here.
 *
 * Nudging is bounded (`maxTries`): a chart with many coincident levels must not
 * loop, and a plate that cannot be placed clear is drawn at its last position
 * rather than dropped — a missing label reads as "there is no level here", which is
 * the one answer this app must not fake.
 */
export function placePlate(o: {
  anchorX: number; anchorY: number; w: number; h: number; canvasW: number;
  gap: number; step: number; placed: PlateBox[]; maxTries?: number; canvasH?: number;
}): PlateBox {
  const { anchorX, anchorY, w, h, canvasW, gap, step, placed } = o;
  const maxTries = o.maxTries ?? 6;

  let left = anchorX + gap;
  if (left + w > canvasW) left = anchorX - gap - w;   // flip leftwards
  left = Math.max(0, Math.min(left, canvasW - w));    // never off either edge

  const hits = (b: PlateBox) =>
    placed.some((q) => b.left < q.right && q.left < b.right
                    && b.top < q.bottom && q.top < b.bottom);

  // VERTICALLY CLAMPED, exactly like the horizontal clamp above, and for the same reason.
  // Nudging is unconditionally UPWARD on the first try (`dir = -1` at i = 0), so a level in
  // the top ~17px of the pane whose plate collides was pushed to a negative `top` and painted
  // off the canvas entirely. That is a SILENT LOSS of a level label -- the one answer this
  // function's own docstring says the app must not fake -- and t_945f289e made it reachable:
  // the week box's caption reserves rows at the box's top edge, and the box's top edge is the
  // running high, so a ray priced just under it collides exactly there. Measured: box lo=195
  // hi=211 on a 212px pane, rays at 196/198/200/202 all landed at top -7.5 to -13.5, all four
  // on-pane with no box.
  const clampTop = (t: number) => (o.canvasH == null
    ? t
    : Math.max(0, Math.min(t, o.canvasH - h)));

  // Default position: just ABOVE the level line, matching the original look.
  const base = clampTop(anchorY - h - 2);
  let box: PlateBox = { left, right: left + w, top: base, bottom: base + h, clear: true };
  // A CLAMPED CANDIDATE THAT REPEATS ONE ALREADY TESTED IS SKIPPED, NOT SPENT. Without this
  // the clamp above turns into a second defect: near the pane's top edge every upward rung
  // clamps back onto the same `top`, so i = 0, 2, 4 all re-test one colliding position and
  // burn half the budget, and three same-column plates then land on the identical exhaustion
  // row -- distinct-but-half-off-pane traded for exactly-superimposed. Skipping repeats keeps
  // the full downward search depth, which is the only direction with room there.
  const tried = new Set<number>([base]);
  for (let i = 0, spent = 0; spent < maxTries && hits(box); i++) {
    // Alternate up/down, growing: keeps a plate near its own line rather than
    // marching every label in one direction off the pane.
    const dir = i % 2 === 0 ? -1 : 1;
    const top = clampTop(base + dir * Math.ceil((i + 1) / 2) * step);
    if (tried.has(top)) {
      // Both directions exhausted into repeats: there is nowhere left to look, and looping
      // to `i` forever would hang the frame.
      if (i > 2 * maxTries) break;
      continue;
    }
    tried.add(top);
    spent += 1;
    box = { left, right: left + w, top, bottom: top + h, clear: true };
  }
  // Reported, not enforced: this function still returns a position even when none is
  // clear (a dropped LEVEL label reads as "no level here"). `clear` lets a caller
  // drawing many zones choose to withhold instead — see `_label`'s `dropIfObstructed`.
  return { ...box, clear: !hits(box) };
}
